import { useEffect, useState } from "react";
import { useLocation, unstable_usePrompt } from "react-router-dom";

import {
  useDebounce,
  useHookForm,
  useMerchantApiMutation,
  useMerchantApiQuery,
  useMerchantApiQueryWithParams,
  useReactRouterLink,
} from "../../../../../hooks";
import {
  ICreateGatewayTransfer,
  ICreateGatewayTransferBeneficiary,
  IHookForm,
  IPageAndSearchRequest,
  IResolveGatewayMerchantResponse,
  ISwAppSupportedCountry,
  ISwAppSupportedCurrency,
  ISwAppSupportedPaymentGateway,
  IViewGatewayTransferBeneficiary,
  NoMeta,
  PagedMeta,
} from "../../../../../interfaces-and-types";
import { generateRandomString, globalHandlerUtils } from "../../../../../utils";

import { singleGatewayTransferSchema } from "./singleGatewayTransferValidationSchema";
import { View } from "./View";
import { SWITCHAPP_GATEWAY } from "../../../../../constants";

export type TNewOrBeneficiaryRecipient = "new" | "beneficiary";

export interface ISingleTransferViewProps
  extends IHookForm<ICreateGatewayTransfer> {
  navigateToBatchList: () => void;
  isBatchEditMode?: boolean;

  beneficiaries: IViewGatewayTransferBeneficiary[];
  setSearchBeneficiaryText: (search: string | null) => void;
  selectBeneficiary: (beneficiary: IViewGatewayTransferBeneficiary) => void;
  clearSelectedBeneficiary: () => void;
  isLoadingBeneficiaries: boolean;

  newOrBeneficiaryView: TNewOrBeneficiaryRecipient;
  setNewOrBeneficiaryView: (name: TNewOrBeneficiaryRecipient) => void;

  isLoadingSupportedCountries: boolean;
  swAppSupportedCountries: ISwAppSupportedCountry[];

  isLoadingSupportedCurrencies: boolean;
  swAppSupportedCurrencies: ISwAppSupportedCurrency[];

  isLoadingSupportedGateways: boolean;
  supportedGateways: ISwAppSupportedPaymentGateway[];
  formValues: ICreateGatewayTransfer;
  isDirty: boolean;

  isGatewayMerchantResolved: boolean;
  setGatewayMerchantResolved: (isResolved: boolean) => void;
  setResolvedGatewayMerchantDetails: (
    resolvedDetails: IResolveGatewayMerchantResponse
  ) => void;
}

interface ICountrySelect {
  country: string;
}

interface ICountryAndCurrencySelect extends ICountrySelect {
  currency: string;
}

export const SingleGatewayTransferForm = (props: {
  appendSingleGatewayTransfer: (
    singleGatewayTransfer: ICreateGatewayTransfer
  ) => void;
  gatewayTransfersList: ICreateGatewayTransfer[];

  // for inline batch editing
  isBatchEditMode?: boolean;
  initialBatchEntryValues?: ICreateGatewayTransfer;
}) => {
  const { navigate } = useReactRouterLink();
  const location = useLocation();

  const [searchBeneficiaryText, setSearchBeneficiaryText] = useState<
    string | null
  >("");

  const {
    isSuccess: isSuccessCreateGatewayTransferBeneficiary,
    mutate: createGatewayTransferBeneficiary,
    data: serviceResponseViewBeneficiary,
  } = useMerchantApiMutation<
    ICreateGatewayTransferBeneficiary,
    IViewGatewayTransferBeneficiary
  >("createGatewayTransferBeneficiary", [
    "getGatewayTransferBeneficiaries",
  ]).usePostMutation;

  const {
    isLoading: isLoadingBeneficiaries,
    isError: isErrorBeneficiaries,
    isSuccess: isSuccessBeneficiaries,
    data: beneficiaries,
    meta: beneficiariesMeta,
  } = useMerchantApiQueryWithParams<
    IPageAndSearchRequest,
    IViewGatewayTransferBeneficiary[],
    PagedMeta
  >([
    "getGatewayTransferBeneficiaries",
    {
      page: 1,
      page_size: 20,
      search: searchBeneficiaryText,
    },
  ]);

  const [isGatewayMerchantResolved, setGatewayMerchantResolved] =
    useState(false);

  const {
    isLoading: isLoadingSupportedCountries,
    data: swAppSupportedCountries,
  } = useMerchantApiQuery<ISwAppSupportedCountry[]>("getSupportedCountries");

  const initialFormValues: ICreateGatewayTransfer =
    props.initialBatchEntryValues ?? {
      reference: `SWAPP-GW-${generateRandomString(15)}`,
      beneficiary_id: null,
      country: "NG",
      recipient_gateway_code: "switchapp",
      merchant_id_or_code: "@",
      amount: 0,
      currency: "NGN",
      description: "",
      save_as_beneficiary: true,
      wallet_account_tags: [],

      isBeneficiaryResolved: false,
      isLoadingResolveBeneficiary: false,
      isErrorResolveBeneficiary: false,

      merchantNameHelper: null,
      gatewayNameHelper: null,
      fullNameHelper: null,
    };

  const handleAppendSingleTransferSubmit = async (
    data: ICreateGatewayTransfer
  ) => {
    const duplicateRefExists =
      !props.isBatchEditMode &&
      props.gatewayTransfersList.find(
        (trsf) => trsf.reference === data.reference
      );

    if (duplicateRefExists) {
      setError("reference", {
        message: "Duplicate transfer reference!",
      });
      return; // duplicate
    }

    if (!isGatewayMerchantResolved) {
      setError("merchant_id_or_code", {
        message: "Gateway merchant details not resolved!",
      });
      return; // duplicate
    }

    if (formValues.save_as_beneficiary) {
      /*await*/ createGatewayTransferBeneficiary(formValues); // mutateAsync cannot be awaited
    }

    props.appendSingleGatewayTransfer({
      // beneficiary_id: serviceResponseViewBeneficiary?.data.id, // may retrieve previous value b/c of mutateAsync behaviour

      ...data,
      amount: Number(data.amount), // amount is string from text box and needs conversion in <CurrencyDisplay />
      isBeneficiaryResolved: isGatewayMerchantResolved,
    });

    reset({
      ...initialFormValues,
      country: data.country, // to save time from having to re-select
      currency: data.currency,
    });

    navigateToBatchList();
  };

  const handleError = (errors: any) => globalHandlerUtils.handleError(errors);

  const {
    onFormSubmit,
    errors,
    register,
    reset,
    control,
    setValue,
    setError,
    // clearErrors,
    watch,
    isDirty,
  } = useHookForm({
    initialFormValues,
    validationSchema: singleGatewayTransferSchema,
    onValid: handleAppendSingleTransferSubmit,
    onInvalid: handleError,
  });

  const formValues = watch();

  //Error: useBlocker must be used within a data router.  See https://reactrouter.com/routers/picking-a-router.
  // unstable_usePrompt({
  //   when: isDirty,
  //   message:
  //     "Are you sure you want to leave? Your changes have not been submitted!",
  // });

  const debouncePeriodElapsed = useDebounce();

  const { isLoading: isLoadingGatewayList, data: supportedGatewaysList } =
    useMerchantApiQueryWithParams<
      ICountryAndCurrencySelect,
      ISwAppSupportedPaymentGateway[],
      NoMeta
    >(
      [
        "getSupportedGateways",
        {
          country: formValues.country,
          currency: formValues.currency,
        },
      ],
      {
        enabled:
          debouncePeriodElapsed &&
          !!formValues.country &&
          !!formValues.currency,
      }
    );

  const {
    isLoading: isLoadingSupportedCurrencies,
    data: swAppSupportedCurrencies,
  } = useMerchantApiQueryWithParams<
    ICountrySelect,
    ISwAppSupportedCurrency[],
    NoMeta
  >(
    [
      "getSupportedCurrencies",
      {
        country: formValues.country,
      },
    ],
    {
      enabled: !!formValues.country,
    }
  );

  useEffect(() => {
    if (!isGatewayMerchantResolved) {
      setValue("merchantNameHelper", null);
      setValue("gatewayNameHelper", null);
    }
  }, [isGatewayMerchantResolved]);

  const setResolvedGatewayMerchantDetails = (
    resolvedDetails: IResolveGatewayMerchantResponse
  ) => {
    setValue("merchantNameHelper", resolvedDetails.merchant_name);
    setValue("gatewayNameHelper", resolvedDetails.gateway_name);
  };

  const navigateToBatchList = () => {
    navigate(location.pathname.replace("/single", ""));
  };

  const [newOrBeneficiaryView, setNewOrBeneficiaryView] =
    useState<TNewOrBeneficiaryRecipient>("beneficiary");

  useEffect(() => {
    if (
      isSuccessBeneficiaries &&
      beneficiaries &&
      beneficiaries?.length === 0 &&
      !searchBeneficiaryText
    )
      setNewOrBeneficiaryView("new");
  }, [isSuccessBeneficiaries]);

  // always clear search params after navigating away from list
  useEffect(() => setSearchBeneficiaryText(null), [newOrBeneficiaryView]);

  const selectBeneficiary = (beneficiary: IViewGatewayTransferBeneficiary) => {
    reset({
      ...formValues,

      // ...beneficiary, // inserts benef description into transfer description
      beneficiary_id: beneficiary.id,
      country: beneficiary.country,
      recipient_gateway_code: beneficiary.recipient_gateway_code,
      merchant_id_or_code: beneficiary.merchant_id_or_code,
      currency: beneficiary.currency,
      save_as_beneficiary: false,

      gatewayNameHelper: beneficiary.gateway_name,
      merchantNameHelper: beneficiary.merchant_name,
      fullNameHelper: beneficiary.full_name,
    });
    // setGatewayMerchantResolved(true);
    setSearchBeneficiaryText("");
  };

  const clearSelectedBeneficiary = () => {
    reset({
      // ...initialFormValues,
      ...formValues,
      beneficiary_id: null,
      recipient_gateway_code: undefined,
      merchant_id_or_code: undefined,
      gatewayNameHelper: null,
      merchantNameHelper: null,
      fullNameHelper: null,
    });

    setGatewayMerchantResolved(false);
  };

  return (
    <View
      {...props}
      navigateToBatchList={navigateToBatchList}
      isBatchEditMode={props.isBatchEditMode}
      beneficiaries={beneficiaries || []}
      isLoadingBeneficiaries={isLoadingBeneficiaries}
      setSearchBeneficiaryText={setSearchBeneficiaryText}
      selectBeneficiary={selectBeneficiary}
      clearSelectedBeneficiary={clearSelectedBeneficiary}
      newOrBeneficiaryView={newOrBeneficiaryView}
      setNewOrBeneficiaryView={setNewOrBeneficiaryView}
      isLoadingSupportedCountries={isLoadingSupportedCountries}
      swAppSupportedCountries={swAppSupportedCountries || []}
      isLoadingSupportedCurrencies={isLoadingSupportedCurrencies}
      swAppSupportedCurrencies={swAppSupportedCurrencies || []}
      isLoadingSupportedGateways={isLoadingGatewayList}
      supportedGateways={
        [SWITCHAPP_GATEWAY]
        // !supportedGatewaysList
        //   ? []
        //   : [SWITCHAPP_GATEWAY, ...supportedGatewaysList]
      }
      isGatewayMerchantResolved={isGatewayMerchantResolved}
      setGatewayMerchantResolved={setGatewayMerchantResolved}
      setResolvedGatewayMerchantDetails={setResolvedGatewayMerchantDetails}
      // batch
      formValues={formValues}
      isDirty={isDirty}
      handleSubmit={onFormSubmit}
      register={register}
      errors={errors}
      control={control}
    />
  );
};
